import Point from "../models/Point";
import { MutableRefObject } from "react";
import Logger from "../Logger";
import Browser from "../browser";
import BoundaryRect from "../models/BoundaryRect";
import { noItx } from "../translationItx/itx";

const logger = new Logger();

var scaleProviderElement: any = null;
//const showTimer = useRef(0) as any;

function initScaleProvider(elm: HTMLElement) {
  var parent = getDrawingAreaElement(elm);

  while (parent && !parent.getAttribute(noItx("data-scale"))) {
    const pp = parent.parentElement;
    if (pp) {
      parent = pp;
    } else {
      logger.error("no scaleProviderElement found");
    }
  }
  scaleProviderElement = parent;
}
export function getDrawingAreaElement(elm: HTMLElement): HTMLElement | undefined {
  var res = elm.closest(".pane") as HTMLAnchorElement; // no itx
  res = res?.querySelector(".mainDrawingArea") as HTMLAnchorElement; // no itx
  return res;
}

export function getDrawingAreaPos(elm: HTMLElement): DOMRect | undefined {
  const elmAreaElement = getDrawingAreaElement(elm);
  if (elmAreaElement) {
    const r = elmAreaElement.getBoundingClientRect();
    if (r.width != 0) {
      return r;
    }
  }
  logger.error("getDrawingAreaElement failed");
  return undefined;
}

export function pointToDrawingArea(elm: HTMLElement, p: Point): Point | undefined {
  const parentPos = getDrawingAreaPos(elm);
  if (parentPos) {
    var x = p.x;
    var y = p.y;
    var scale = getScale(elm);

    x = (x - parentPos.x) / scale;
    y = (y - parentPos.y) / scale;

    return new Point(x, y);
  } else {
    return undefined;
  }
}

export function getObjectRectInDrawingArea(elm: HTMLElement): DOMRect {
  // children[0] == selectFrame
  // ... to overcome problems with positioning with marginLeft:"-50%"
  //
  const r = elm.children[0]?.getBoundingClientRect();

  const parentPos = getDrawingAreaPos(elm);
  if (parentPos) {
    var x = r.left;
    var y = r.top;
    var scale = getScale(elm);

    x = (x - parentPos.x) / scale;
    y = (y - parentPos.y) / scale;

    return new DOMRect(x, y, r.width / scale, r.height / scale);
  } else {
    return r;
  }
}

export function eventPointToDrawingArea(event: any, mouseX?: number, mouseY?: number): Point | undefined {
  var elm = event.currentTarget;
  if (elm == null || elm.tagName == undefined) {
    elm = event.target;
  }

  const drawArea = getDrawingAreaElement(elm);

  const parentPos = getDrawingAreaPos(elm);
  if (parentPos && drawArea) {
    initScaleProvider(elm);

    var x = mouseX || event.clientX || event.touches[0]?.clientX;
    var y = mouseY || event.clientY || event.touches[0]?.clientY;

    var scale = getScale(elm);

    x = (x - parentPos.x) / scale + drawArea.scrollLeft;
    y = (y - parentPos.y) / scale + drawArea.scrollTop;

    return new Point(x, y);
  } else {
    return undefined;
  }
}

export function getChildElement(parent: HTMLElement, id: string): HTMLElement | null {
  return parent.querySelector(`[id='${id}']`);
}

export function pointInRect(r: DOMRect, p: Point): boolean {
  return p.x > r.x && p.x < r.x + r.width && p.y > r.y && p.y < r.y + r.height;
}

export function pointInBounderyRect(r: BoundaryRect, p: Point): boolean {
  return p.x > r.min.x && p.x < r.max.x && p.y > r.min.y && p.y < r.max.y;
}

export function getScale(elm: HTMLElement) {
  initScaleProvider(elm);

  var scale = 1;
  if (scaleProviderElement) {
    scale = scaleProviderElement.getAttribute("data-scale"); // no itx
  } else {
    logger.error("getScale, no scaleProviderElement");
  }
  return scale;
}

export function getLeftTopFromStyle(elm: HTMLElement): Point {
  const style = elm.style;
  const left = parseFloat(style.left);
  const top = parseFloat(style.top);
  return new Point(left, top);
}

export function getCenterFromStyle(elm: HTMLElement): Point {
  const p = getLeftTopFromStyle(elm);
  const c = getCenterOffsetFromStyle(elm);
  p.x += c.x;
  p.y += c.y;

  return p;
}

export function getWidthFromStyle(elm: HTMLElement): number {
  const w = parseFloat(elm.style.width);
  return w;
}

export function getHeightFromStyle(elm: HTMLElement): number {
  const h = parseFloat(elm.style.height);
  return h;
}

export function getCenterOffsetFromStyle(elm: HTMLElement): Point {
  const centerX = parseFloat(elm.getAttribute("data-centerx") as string); // no itx
  const centerY = parseFloat(elm.getAttribute("data-centery") as string); // no itx
  return new Point(centerX || 0, centerY || 0);
}

export function setLeftTopInStyle(elm: HTMLElement | undefined, p: Point) {
  if (elm) {
    elm.style.left = `${p.x}px`;
    elm.style.top = `${p.y}px`;
  }
}

export function connectionHoverStart(
  showTimer: MutableRefObject<any>,
  deny: boolean,
  ...rest: (HTMLElement | null | undefined)[]
) {
  rest.forEach((item) => {
    if (item) {
      if (deny) {
        item.classList.add("connectionDeny"); // no itx
        if (showTimer.current) {
          clearTimeout(showTimer.current);
        }
        showTimer.current = setTimeout(() => {
          showTimer.current = 0;
          item.classList.remove("connectionDeny"); // no itx
        }, 2000);
      } else {
        item.classList.add("connectionHover"); // no itx
      }
    }
  });
}

export function connectionHoverEnd(...rest: (HTMLElement | null | undefined)[]) {
  rest.forEach((item) => {
    if (item) {
      item.classList.remove("connectionDeny"); // no itx
      item.classList.remove("connectionHover"); // no itx
    }
  });
}

//
// returns true if at least one column item is found
//
export function allignColumnTreeview(columnName: string, rootElement?: HTMLElement, maxWidth?: number): boolean {
  var maxSize = 0;
  const col1Elements = rootElement
    ? rootElement.querySelectorAll(`.${columnName}`)
    : document.getElementsByClassName(columnName);

  for (let i = 0; i < col1Elements.length; i++) {
    const el: HTMLElement = col1Elements[i] as any;
    el.style.minWidth = "10px";
    el.style.flexShrink = "0";
    (el.style as any).overflow = undefined;
    (el.style as any).textOverflow = undefined;
  }

  for (let i = 0; i < col1Elements.length; i++) {
    const el = col1Elements[i];
    maxSize = Math.max(maxSize, (el as any).offsetWidth); // is before zooming !!!
  }

  if (maxWidth) {
    maxSize = Math.min(maxSize, maxWidth);
  }

  maxSize = Math.ceil(maxSize) + 1; // 1px weil "offsetWidth" anscheind gerundet ist

  for (let i = 0; i < col1Elements.length; i++) {
    const el: HTMLElement = col1Elements[i] as any;
    el.style.minWidth = `${maxSize}px`;
    (el.style as any).flexShrink = undefined;
    el.style.textOverflow = "ellipsis"; // no itx
    el.style.overflow = "hidden"; // no itx
  }

  return col1Elements.length > 0;
}

export function allignColumn1(columnName: string, rootElement: HTMLElement | null) {
  if (rootElement) {
    var maxSize = 0;
    const col1Elements = rootElement.querySelectorAll(`.${columnName}`);

    for (let i = 0; i < col1Elements.length; i++) {
      const el: HTMLElement = col1Elements[i] as any;
      el.style.minWidth = "10px";
    }

    for (let i = 0; i < col1Elements.length; i++) {
      const el = col1Elements[i];
      maxSize = Math.max(maxSize, (el as any).offsetWidth); // is before zooming !!!
    }

    maxSize = Math.trunc(maxSize) + 1;

    /// maxSize += 10; // 5px padding left & right
    maxSize = Math.min(maxSize, 400);

    if (maxSize > window.innerWidth - 20) {
      maxSize = window.innerWidth - 40;
    }

    for (let i = 0; i < col1Elements.length; i++) {
      const el: HTMLElement = col1Elements[i] as any;
      el.style.minWidth = `${maxSize}px`;
    }
  }
}

export function allignColumn(columnName: string, inlineSize?: number, rootElement?: HTMLElement) {
  var maxSize = 0;
  const col1Elements = rootElement
    ? rootElement.querySelectorAll(`.${columnName}`)
    : document.getElementsByClassName(columnName);

  for (let i = 0; i < col1Elements.length; i++) {
    const el: HTMLElement = col1Elements[i] as any;
    el.style.minWidth = "10px";
  }

  for (let i = 0; i < col1Elements.length; i++) {
    const el = col1Elements[i];
    maxSize = Math.max(maxSize, (el as any).offsetWidth); // is before zooming !!!
  }

  /// maxSize += 10; // 5px padding left & right
  maxSize = Math.min(maxSize, 400);

  if (maxSize > window.innerWidth - 20) {
    maxSize = window.innerWidth - 40;
  }

  for (let i = 0; i < col1Elements.length; i++) {
    const el: HTMLElement = col1Elements[i] as any;
    el.style.minWidth = `${maxSize}px`;
    if (inlineSize) {
      el.style.inlineSize = `${maxSize - inlineSize}px`;
    } else {
      el.style.removeProperty("inline-size");
    }
  }
}

export function allignElement(columnName: string) {
  var maxSize = 0;
  const col1Elements = document.getElementsByClassName(columnName);

  for (let i = 0; i < col1Elements.length; i++) {
    const el: HTMLElement = col1Elements[i] as any;
    el.style.minWidth = "10px";
  }

  for (let i = 0; i < col1Elements.length; i++) {
    const el = col1Elements[i];
    maxSize = Math.max(maxSize, (el as any).offsetWidth); // is before zooming !!!
  }

  maxSize += 10; // 5px padding left & right

  for (let i = 0; i < col1Elements.length; i++) {
    const el: HTMLElement = col1Elements[i] as any;
    el.style.minWidth = `${maxSize}px`;
    el.style.inlineSize = `${maxSize - 10}px`;
    el.style.overflowWrap = `break-word`;
  }
}

export function calcTextWidth(text: string): number {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (ctx) {
    let textSize = ctx.measureText(text);
    return textSize.width;
  } else {
    return 100;
  }
}

//
// move element to to front (first position in parents childen collection)
//
export function bringElementToTop(elm: HTMLElement | undefined | null) {
  if (elm) {
    elm.parentElement?.appendChild(elm);
  }
}

export function dataUrlToImage(dataUrl: string | undefined): Promise<HTMLImageElement | undefined> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    if (dataUrl) {
      image.src = dataUrl;
      image.onload = () => {
        resolve(image);
      };
      image.onerror = (error) => {
        console.error("Image loading error:", dataUrl, error);
        resolve(undefined);
      };
    } else {
      resolve(image);
    }
  });
}

export async function dataUrlToCanvas(dataUrl: string | undefined): Promise<HTMLCanvasElement | undefined> {
  const image = await dataUrlToImage(dataUrl);
  if (!image) return undefined;

  const canvas = await imageToCanvas(image);
  return canvas;
}

export async function imageToCanvas(image: HTMLImageElement): Promise<HTMLCanvasElement | undefined> {
  if (!image) return undefined;

  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;

  const context = canvas.getContext("2d");
  context?.drawImage(image, 0, 0, image.width, image.height);

  return canvas;
}

export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to convert canvas to Blob"));
        }
      },
      "image/jpeg",
      0.97,
    );
  });
}

export async function downloadDebugCanvas(canvas3D: HTMLCanvasElement, name: string): Promise<void> {
  const blob = await canvasToBlob(canvas3D);
  if (blob) {
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.jpg`;
    a.click();
  }
}

export function getCumulativeScale(element: HTMLElement | undefined | null): number {
  if (!element) {
    return 1;
  }

  let current: HTMLElement | null = element;
  let scale = 1;

  while (current) {
    const style = window.getComputedStyle(current);
    const transform = style.transform;

    if (transform && transform !== "none") {
      const match = transform.match(/matrix\(([^)]+)\)/);
      if (match) {
        const values = match[1].split(",").map((v) => parseFloat(v.trim()));
        if (values.length >= 6) {
          // matrix(a, b, c, d, tx, ty) → scaleX = a, scaleY = d
          scale *= values[0];
        }
      }

      const match2 = transform.match(/scale\(([^)]+)\)/);
      if (match2) {
        const s = match2[1];
        scale *= parseFloat(s);
      }
    }

    current = current.parentElement;
  }

  return scale;
}

//
// Statisches Canvas für Textmessung - wird einmalig erstellt und wiederverwendet
//
let _textMeasurementCanvas: HTMLCanvasElement | null = null;
let _textMeasurementContext: CanvasRenderingContext2D | null = null;

//
// Initialisiert das statische Canvas für Textmessung (falls noch nicht vorhanden)
//
function initTextMeasurementCanvas(): void {
  if (!_textMeasurementCanvas) {
    _textMeasurementCanvas = document.createElement("canvas");
    _textMeasurementContext = _textMeasurementCanvas.getContext("2d");
  }
}

//
// Gibt das statische Canvas frei (für Release/Cleanup)
//
export function releaseTextMeasurementCanvas(): void {
  _textMeasurementCanvas = null;
  _textMeasurementContext = null;
}

//
// Textbreiten-Berechnung für PDF-Komponenten.
// Die bisherige PDFKit-Integration ist im aktuellen Setup nicht verfügbar
// (@react-pdf/pdfkit exportiert kein PDFFont), daher wird hier sicher auf die
// Canvas-basierte Messung zurückgefallen.
//
export function measureTextForPDF(
  text: string,
  fontSizeCss: string = "16px",
  options?: { fontFamily?: string; fontWeight?: number | string; fontStyle?: string },
): { width: number; height: number } {
  try {
    const fontFamily = options?.fontFamily ?? "Helvetica";
    const fontWeight = options?.fontWeight;
    const fontStyle = options?.fontStyle;
    const measured = measureText(text, fontSizeCss);

    if (fontFamily && (fontWeight || fontStyle)) {
      return {
        width: measured.width * 0.98,
        height: measured.height,
      };
    }

    return measured;
  } catch (error) {
    console.warn("measureTextForPDF fallback:", error);
    return measureText(text, fontSizeCss);
  }
}

//
// Textbreiten-Berechnung für PDF-Komponenten (mit statischem Canvas)
// Veraltet für PDF: measureTextForPDF bevorzugen.
//
export function measureText(text: string, fontSizeCss: string = "16px"): { width: number; height: number } {
  // Statisches Canvas initialisieren falls nötig
  initTextMeasurementCanvas();
  const context = _textMeasurementContext;

  // Setze die Font-Eigenschaften entsprechend der PDF-Spezifikation
  if (context) {
    context.font = `${fontSizeCss} Helvetica, Arial, sans-serif`;
  }

  try {
    if (context) {
      const metrix = context.measureText(text);
      let width = metrix.width;
      let height = metrix.actualBoundingBoxAscent + metrix.actualBoundingBoxDescent;

      // console.log(`Text: "${text}" | Canvas: ${width}px | FontSize: ${fontSize}pt`);

      // console.log(`Korrigiert: ${width}pt (Faktor: ${pdfCorrectionFactor})`);
      return { width, height };
    }
  } catch (error) {
    console.error("measureTextWidthForPDF error:", error);
  }

  // Fallback mit verschiedenen Faktoren zum Testen
  const fallbackWidth = text.length * parseFloat(fontSizeCss) * 0.7; // Erhöhter Faktor
  return { width: fallbackWidth, height: parseFloat(fontSizeCss) * 1.2 };
}

// Gibt "png", "jpg" oder undefined für eine dataURL zurück
export function sniffDataUrlFormat(dataUrl: string | undefined): "png" | "jpg" | undefined {
  //no itx
  if (!dataUrl) return undefined;
  if (dataUrl.startsWith("data:image/png")) return "png"; // no itx
  if (dataUrl.startsWith("data:image/jpeg") || dataUrl.startsWith("data:image/jpg")) return "jpg"; // no itx
  return undefined;
}

// Gibt "png", "jpg" oder undefined für einen Blob zurück (ruft sniffDataUrlFormat auf)
export async function sniffBlobFormat(blob: Blob | undefined): Promise<"png" | "jpg" | undefined> {
  // no itx
  if (!blob) return undefined;
  // Versuche als DataURL zu lesen
  const dataUrl = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
  return sniffDataUrlFormat(dataUrl);
}

function parseColorToRGB(color: string): { r: number; g: number; b: number } | null {
  if (color.startsWith("#")) {
    let hex = color.replace("#", "");
    if (hex.length === 3)
      hex = hex
        .split("")
        .map((x) => x + x)
        .join("");
    if (hex.length !== 6) return null;
    const num = parseInt(hex, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  }
  const m = color.match(/rgba?\\(\\d+),\\s*(\\d+),\\s*(\\d+)/);
  if (m) return { r: +m[1], g: +m[2], b: +m[3] };
  return null;
}

// Gibt "#000" (schwarz) oder "#fff" (weiß) für beste Lesbarkeit zurück
export function getContrastTextColor(bgColor: string): "#000" | "#fff" {
  const rgb = parseColorToRGB(bgColor);
  if (!rgb) return "#000"; // no itx
  // YIQ-Formel für Helligkeit
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return yiq >= 128 ? "#000" : "#fff"; // no itx
}

// Wandelt ein PNG mit schwarzer Grafik in ein PNG mit weißer Grafik um (auf transparentem Hintergrund)
export async function invertPngBlackToWhite(dataUrl: string): Promise<string | undefined> {
  const img = await dataUrlToImage(dataUrl);
  if (img) {
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return dataUrl;
    ctx.drawImage(img, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = imageData.data;
    for (let i = 0; i < d.length; i += 4) {
      // Wenn Pixel ist schwarz (Alpha > 0, R,G,B < 64): mache weiß
      if (d[i + 3] > 0 && d[i] < 64 && d[i + 1] < 64 && d[i + 2] < 64) {
        d[i] = 255;
        d[i + 1] = 255;
        d[i + 2] = 255;
      }
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL("image/png"); // no itx
  }

  return undefined;
}

export function srcToImageType(src: string): "png" | "jpg" | undefined {
  if (src.length < 100) {
    const s1 = src.toLowerCase();
    if (s1.endsWith(".png")) {
      return "png"; // no itx
    } else {
      if (s1.endsWith(".jpg")) {
        return "jpg"; // no itx
      }
    }
  }

  return sniffDataUrlFormat(src);
}

export async function reduceResolution(
  src: string,
  targetHeight: number,
  targetWidth: number,
): Promise<
  | {
      imgSrc: string;
      imgWidth: number;
      imgHeight: number;
    }
  | undefined
> {
  // src = dataUrl
  // targetHeight, targetWidth in pt (72pt = 1 inch)
  // return src, width, height in pt

  try {
    const img = await dataUrlToImage(src);

    if (!img) return undefined;

    if (targetHeight <= 0 && targetWidth <= 0) {
      logger.devError("targetHeight <= 0 && targetHeight <= 0");
      targetHeight = img.height;
      targetWidth = img.width;
    } else if (targetHeight <= 0) {
      targetHeight = (targetWidth * img.height) / img.width;
    } else if (targetWidth <= 0) {
      targetWidth = (targetHeight * img.width) / img.height;
    }

    const TARGET_DPI = 300;
    const pxWidth = Math.round(targetWidth * (TARGET_DPI / 72));
    const pxHeight = Math.round(targetHeight * (TARGET_DPI / 72));

    let imageType = srcToImageType(src);

    if ((img && img.width * 0.9 > pxWidth) || img.height * 0.9 > pxHeight) {
      const canvas = document.createElement("canvas");
      canvas.width = pxWidth;
      canvas.height = pxHeight;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        if (imageType !== "png") {
          // Canvas mit weiß füllen
          // für png nicht füllen / transparent lassen
          //
          ctx.fillStyle = "#fff";
          ctx.fillRect(0, 0, pxWidth, pxHeight);
        }
        ctx.drawImage(img, 0, 0, pxWidth, pxHeight);
      }

      src = canvas.toDataURL(imageType === "png" ? "image/png" : "image/jpeg", 0.97);
    }

    return {
      imgSrc: src,
      imgWidth: pxWidth,
      imgHeight: pxHeight,
    };
  } catch (err) {
    logger.error(`Error reducing resolution of image ${src}: ${err} `);
    return {
      imgSrc: src,
      imgWidth: targetHeight,
      imgHeight: targetWidth,
    };
  }
}

export async function loadImageFromUrl(url: string): Promise<HTMLImageElement | undefined> {
  const promise = new Promise<HTMLImageElement | undefined>((resolve, reject) => {
    const img = new Image();
    img.src = url;
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject(new Error(`Failed to load image from ${url}`));
    };
  });
  return promise;
}

export function freezeTableColumnWidths(table: HTMLElement) {
  if (!table) return;

  // 1) Auto-Layout einmal messen
  table.style.tableLayout = "auto";

  const headerRow = table.querySelector("thead");
  if (!headerRow) return;

  const cells = Array.from(headerRow.children);
  const widths = cells.map((c) => c.getBoundingClientRect().width);

  // 2) colgroup aufbauen + Breiten setzen
  let colgroup = table.querySelector("colgroup"); // no itx
  if (!colgroup) {
    colgroup = document.createElement("colgroup");
    table.prepend(colgroup);
  }
  colgroup.innerHTML = "";
  widths.forEach((w) => {
    const col = document.createElement("col");
    col.style.width = `${Math.round(w)}px`;
    colgroup.appendChild(col);
  });

  // 3) Jetzt einfrieren
  table.style.tableLayout = "fixed"; // no itx
}

// Beispiel:
//freezeTableColumnWidths(document.querySelector("#t"));
